import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import {ComponentManagerService, FormService, NotificationService} from "@fgpp-ui/components";
import {FormControl, FormGroup} from "@angular/forms";
import {DateFilterOptions} from "../../enums/date-filter-options.enum";
import {QuickSearchService} from "../../services/quick-search.service";
import {SearchFilter} from "../../../../shared/models/search-filter.model";
import {IQuickSearch} from "../../interfaces/quick-search.interface";
import dayjs, {Dayjs, isDayjs} from "dayjs";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-quick-search-modal',
  templateUrl: './quick-search-modal.component.html',
  styleUrls: ['./quick-search-modal.component.scss'],
  providers: [FormService, ComponentManagerService]
})
export class QuickSearchModalComponent implements OnInit, AfterViewInit {

  @ViewChild('referenceIdInput') referenceIdInput: ElementRef;

  formService = inject(FormService);
  quickSearchService = inject(QuickSearchService);
  notification = inject(NotificationService);
  translate = inject(TranslateService);
  elementRef = inject(ElementRef);
  gridTemplate = '\'a b c\'';
  gridColumns = 3;
  currentRoute: string = '';

  searchForm = new FormGroup({
    referenceIdField: new FormControl<string>(''),
    referenceIdValue: new FormControl(''),
    amountIdField: new FormControl(''),
    amountMinValue: new FormControl(null),
    amountMaxValue: new FormControl(null),
    fieldIdType: new FormControl(''),
    fieldIdValue: new FormControl(''),
    otherFieldId: new FormControl(''),
    dateRangeType: new FormControl(''),
    dateMinValue: new FormControl<string | null>(null),
    dateMaxValue: new FormControl<string | null>(null)
  });

  dateSelectOptions = [
    {key: DateFilterOptions.LAST_WEEK, value: 'navigation_search.search_box.search-modal.date-options.week'},
    {key: DateFilterOptions.LAST_MONTH, value: 'navigation_search.search_box.search-modal.date-options.month'},
    {key: DateFilterOptions.LAST_QUARTER, value: 'navigation_search.search_box.search-modal.date-options.quarter'},
    {key: DateFilterOptions.CUSTOM, value: 'navigation_search.search_box.search-modal.date-options.custom'}
  ];

  amountSelectOption = '/ui/profiles/selectOptions/fieldsValues/QUICK_SEARCH_AMOUNT';
  referenceIdOption = '/ui/profiles/selectOptions/fieldsValues/QUICK_SEARCH_REF';
  otherFieldsOption = '/ui/profiles/selectOptions/fieldsValues/QUICK_SEARCH_FIELDS';

  @Output() onCloseClicked = new EventEmitter<Array<SearchFilter>>();

  async ngOnInit() {
    this.formService.setForm(this.searchForm);
    this.setDefaultFormValues();
    const dateRangeType = this.searchForm.get('dateRangeType')?.value;
    this.handleDateChange(dateRangeType);
  }

  ngAfterViewInit() {
    const inputElement = this.referenceIdInput.nativeElement.querySelector('input');
    if (inputElement) {
      setTimeout(() => inputElement.focus(), 500);
    }
  }

  onDateChange(date) {
    this.handleDateChange(date);
  }

  handleDateChange(dateType: string) {
    const startDateControl = this.searchForm.get('dateMinValue');
    const endDateControl = this.searchForm.get('dateMaxValue');

    if (dateType === DateFilterOptions.CUSTOM) {
      startDateControl?.enable();
      endDateControl?.enable();
    } else {
      startDateControl?.disable();
      startDateControl?.reset();
      endDateControl?.disable();
      endDateControl?.reset();
    }
  }

  onCloseModal() {
    this.onCloseClicked.emit();
  }

  getStartDate(date: Dayjs): string {
    if (!isDayjs(date)) {
      return ''
    }
    const tzOffset = date.toDate().getTimezoneOffset() * -1;
    const adjustedDate = date.add(tzOffset, 'minutes');

    const [datePart, timePart] = adjustedDate.toISOString().split('T');

    return datePart;
  }


  getEndDate(date: Dayjs): string {
    if (!isDayjs(date)) {
      return ''
    }
    const tzOffset = date.toDate().getTimezoneOffset() * -1;
    const adjustedDate = date.add(tzOffset, 'minutes').add(1, 'day').subtract(1, 'millisecond');

    const [datePart, timePart] = adjustedDate.toISOString().split('T');

    return datePart;
  }

  getFormValues() {
    const dateRangeType = this.searchForm.get('dateRangeType').value;
    let startDateISO, endDateISO;
    if (dateRangeType === DateFilterOptions.CUSTOM) {
      if (!this.searchForm.get('dateMinValue')?.value || !this.searchForm.get('dateMaxValue')?.value) {
        this.notification.error(this.translate.instant('messages_center.quick_search.invalid_date_range'));
        return;
      }
      startDateISO = this.getStartDate(dayjs(this.searchForm.get('dateMinValue')?.value));
      endDateISO = this.getEndDate(dayjs(this.searchForm.get('dateMaxValue')?.value));
    } else if (dateRangeType === DateFilterOptions.LAST_WEEK) {
      const date = new Date();
      const today = dayjs(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
      startDateISO = this.getStartDate(today.subtract(6, 'days'));
      endDateISO = this.getEndDate(today);
    } else if (dateRangeType === DateFilterOptions.LAST_MONTH) {
      const date = new Date();
      const today = dayjs(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
      startDateISO = this.getStartDate(today.subtract(29, 'days'));
      endDateISO = this.getEndDate(today);
    } else if (dateRangeType === DateFilterOptions.LAST_QUARTER) {
      const date = new Date();
      const today = dayjs(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
      startDateISO = this.getStartDate(today.subtract(89, 'days'));
      endDateISO = this.getEndDate(today);
    }
    const formValue = {...this.searchForm.getRawValue(), dateMinValue: startDateISO, dateMaxValue: endDateISO};
    return formValue;
  }

  doSearch() {
    const data: IQuickSearch = this.getFormValues();
    const searchCriteria = this.quickSearchService.buildSearchCriteria(data);

    if (this.isFormValid()) {
      this.onCloseClicked.emit(searchCriteria);
    }
  }

  clearFormValues() {
    this.searchForm.setValue({
      dateRangeType: DateFilterOptions.LAST_WEEK,
      dateMinValue: null,
      dateMaxValue: null,
      referenceIdField: 'P_MID',
      referenceIdValue: '',
      amountIdField: '',
      amountMinValue: null,
      amountMaxValue: null,
      fieldIdType: '',
      fieldIdValue: '',
      otherFieldId: ''
    });
    this.searchForm.get('dateMinValue').disable();
    this.searchForm.get('dateMaxValue').disable();
  }

  setDefaultFormValues() {
    this.currentRoute = window.location.href;
    const recentSearchValues = this.quickSearchService.getRecentSearchCriteria();

    if (recentSearchValues && this.currentRoute && this.currentRoute.length > 1 && this.currentRoute.includes('/home/messages-center/recent-searches/messages')) {
      this.searchForm.setValue(this.quickSearchService.getRecentSearchCriteria());
    } else {
      this.clearFormValues();
      this.quickSearchService.clearRecentSearchCriteria();
    }
  }

  onAmountTypeChange(value: string) {
    if (!value) {
      this.searchForm.get('amountMinValue').reset();
      this.searchForm.get('amountMaxValue').reset();
    }
  }

  isFormValid() {
    return this.searchForm.valid;
  }

  onFieldIdTypeChange(value: string) {
    this.searchForm.get('otherFieldId').reset();
    this.searchForm.get('fieldIdValue').reset();
  }

  onReferenceIdTypeChange() {
    this.searchForm.get('referenceIdValue').reset();
  }
}
